import { Link, usePage } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";

import logoBlack from "@/assets/logo_black.svg";
import { type GenderedTextKey, useGenderedText } from "@/lib/genderedText";
import { type Permissions, usePermissions } from "@/lib/permissions";
import {
  asistencias,
  categories,
  centers,
  contacts,
  courses,
  houses,
  sessions,
  users,
  viewModes,
} from "@/routes";
import type { CurrentUser, SharedProps } from "@/types";

type NavItem = { label: string; href: string | null };

function publicNavItems(userId?: number): NavItem[] {
  const items: NavItem[] = [
    { label: "Retiros y Cursos", href: courses.index().url },
    { label: "Contacto", href: contacts.new().url },
  ];

  if (userId != null) {
    items.push({ label: "Mis Cursos", href: users.profile(userId).url });
  }

  return items;
}

function staffNavItems(
  permissions: Permissions,
  t: (key: GenderedTextKey) => string,
): NavItem[] {
  const items: NavItem[] = [{ label: "Cursos", href: courses.index().url }];

  if (permissions.canViewCategoriesNav) {
    items.push({ label: "Tipos de cursos", href: categories.index().url });
  }

  if (permissions.canViewCentersNav) {
    items.push({ label: "Centros", href: centers.index().url });
  }

  if (permissions.canViewHousesNav) {
    items.push({ label: "Casas", href: houses.index().url });
  }

  if (permissions.canViewStatisticsNav) {
    items.push({ label: "Resumen general", href: courses.statistics().url });
  }

  if (permissions.canViewAsistenciaNav) {
    items.push({ label: "Reporte", href: asistencias.index().url });
  }

  if (permissions.canManageUsersNav) {
    items.push({ label: t("usuarios"), href: users.index().url });
  }

  return items;
}

const navItemClass =
  "font-sans font-light text-ink transition-colors hover:text-steel";

const activeNavItemClass =
  "font-sans font-semibold text-steel underline decoration-2 underline-offset-8";

// Active = the nav item whose href best matches the current path. Longest
// match wins so "/courses/statistics" highlights "Resumen general", not "Cursos".
function activeHref(items: NavItem[], currentUrl: string): string | null {
  const path = currentUrl.split("?")[0];
  let best: string | null = null;
  for (const { href } of items) {
    if (!href) continue;
    if (path === href || path.startsWith(`${href}/`)) {
      if (best === null || href.length > best.length) best = href;
    }
  }
  return best;
}

const accountPillClass =
  "flex h-11 items-center justify-center gap-2 rounded-full border-2 border-ink px-4 font-sans text-ink transition-colors hover:bg-ink hover:text-white";

// Highest-priority active staff role, same priority order as Ability.
function staffRoleLabel(
  user: CurrentUser,
  t: (key: GenderedTextKey) => string,
): string {
  if (!user) return "";
  if (user.is_admin) return t("isAdmin");
  if (user.is_center_director) return t("isCenterDirector");
  if (user.is_director) return t("isDirector");
  if (user.is_subscriber) return t("isSubscriber");
  if (user.is_house_master) return t("isHouseMaster");
  if (user.is_house_manager) return t("isHouseManager");
  return "";
}

function hasStaffRole(user: CurrentUser): boolean {
  return !!(
    user?.is_admin ||
    user?.is_director ||
    user?.is_center_director ||
    user?.is_house_manager ||
    user?.is_house_master ||
    user?.is_subscriber
  );
}

export default function Header() {
  const page = usePage<SharedProps>();
  const { currentUser, viewingAsStudent } = page.props;
  const permissions = usePermissions();
  const t = useGenderedText();
  const navItems = permissions.isBackofficeStaff
    ? staffNavItems(permissions, t)
    : publicNavItems(currentUser?.id);
  const active = activeHref(navItems, page.url);
  const canToggleViewMode =
    hasStaffRole(currentUser) && !!currentUser?.is_student;

  return (
    <header className="sticky top-0 z-30 border-ink border-b-2 bg-white">
      <div className="flex h-20 w-full items-center justify-between gap-6 px-4 sm:px-6 lg:px-10">
        <Link href="/" className="shrink-0" aria-label="Inicio">
          <img src={logoBlack} alt="Los Tilos" className="h-12 w-auto" />
        </Link>

        <nav className="hidden items-center min-[900px]:flex">
          {navItems.map((item, i) => (
            <span key={item.label} className="flex items-center">
              {i > 0 && <span className="px-3 text-black/30">|</span>}
              {item.href ? (
                <Link
                  href={item.href}
                  className={
                    item.href === active ? activeNavItemClass : navItemClass
                  }
                  aria-current={item.href === active ? "page" : undefined}
                >
                  {item.label}
                </Link>
              ) : (
                <button type="button" className={navItemClass}>
                  {item.label}
                </button>
              )}
            </span>
          ))}
        </nav>

        <MobileNavDropdown navItems={navItems} active={active} />

        {currentUser ? (
          <ProfileDropdown
            name={currentUser.full_name}
            userId={currentUser.id}
            canToggleViewMode={canToggleViewMode}
            viewingAsStudent={viewingAsStudent}
            staffLabel={staffRoleLabel(currentUser, t)}
            studentLabel={t("isStudent")}
          />
        ) : (
          <Link
            href={sessions.new().url}
            className={`${accountPillClass} min-[900px]:w-44`}
            aria-label="Iniciar sesión"
          >
            <LoginIcon className="h-5 w-5 shrink-0 min-[900px]:hidden" />
            <span className="hidden min-[900px]:inline">Iniciar sesión</span>
          </Link>
        )}
      </div>
    </header>
  );
}

/**
 * Below-900px replacement for the inline nav: a hamburger button sitting in
 * the middle of the header, opening a dropdown card styled like the profile
 * dropdown.
 */
function MobileNavDropdown({
  navItems,
  active,
}: {
  navItems: NavItem[];
  active: string | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative min-[900px]:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Cerrar menú" : "Abrir menú"}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-black/5"
      >
        {open ? (
          <CloseIcon className="h-6 w-6" />
        ) : (
          <HamburgerIcon className="h-6 w-6" />
        )}
      </button>

      {open && (
        <nav className="-translate-x-1/2 absolute left-1/2 mt-2 w-48 overflow-hidden rounded-2xl border border-black/10 bg-white py-1 shadow-lg">
          {navItems.map((item) =>
            item.href ? (
              <Link
                key={item.label}
                href={item.href}
                onClick={() => setOpen(false)}
                className={`block px-4 py-2 font-display text-sm hover:bg-black/5 ${
                  item.href === active
                    ? "font-semibold text-steel"
                    : "font-light text-ink"
                }`}
                aria-current={item.href === active ? "page" : undefined}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.label}
                type="button"
                className="block w-full px-4 py-2 text-left font-display font-light text-ink text-sm hover:bg-black/5"
              >
                {item.label}
              </button>
            ),
          )}
        </nav>
      )}
    </div>
  );
}

function ProfileDropdown({
  name,
  userId,
  canToggleViewMode,
  viewingAsStudent,
  staffLabel,
  studentLabel,
}: {
  name: string;
  userId: number;
  canToggleViewMode: boolean;
  viewingAsStudent: boolean;
  staffLabel: string;
  studentLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${accountPillClass} font-light min-[900px]:w-44`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <UserIcon className="h-5 w-5 shrink-0" />
        <span className="hidden min-w-0 truncate min-[900px]:inline">
          {name}
        </span>
        <ChevronIcon
          className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-48 overflow-hidden rounded-2xl border border-black/10 bg-white py-1 shadow-lg"
        >
          {canToggleViewMode &&
            (viewingAsStudent ? (
              <Link
                href={viewModes.destroy().url}
                method="delete"
                as="button"
                className="block w-full px-4 py-2 text-left font-display text-sm font-light text-ink hover:bg-black/5"
                role="menuitem"
              >
                Ver como {staffLabel}
              </Link>
            ) : (
              <Link
                href={viewModes.create().url}
                method="post"
                as="button"
                className="block w-full px-4 py-2 text-left font-display text-sm font-light text-ink hover:bg-black/5"
                role="menuitem"
              >
                Ver como {studentLabel}
              </Link>
            ))}
          <Link
            href={users.profile(userId).url}
            className="block px-4 py-2 font-display text-sm font-light text-ink hover:bg-black/5"
            role="menuitem"
          >
            Mi cuenta
          </Link>
          <Link
            href={sessions.destroy().url}
            method="delete"
            as="button"
            className="block w-full px-4 py-2 text-left font-display text-sm font-light text-deep-red hover:bg-black/5"
            role="menuitem"
          >
            Cerrar sesión
          </Link>
        </div>
      )}
    </div>
  );
}

function LoginIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25"
      />
    </svg>
  );
}

function HamburgerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 19.5a7.5 7.5 0 0 1 15 0v.75H4.5v-.75Z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
    </svg>
  );
}
